import { createRoute, lazyRouteComponent, notFound } from '@tanstack/react-router';
import { isApiError } from '@/api/errors';
import { appRoute, type RouterContext } from '@/app/layout-routes';
import { requirePermissions } from '@/app/route-guards';
import { currenciesListQuery, currenciesSearchSchema, currencyQuery } from './api';
import { CurrencyNotFound } from './pages/currency-not-found';

async function loadCurrency(context: RouterContext, code: string) {
  try {
    await context.queryClient.ensureQueryData(currencyQuery(code));
  } catch (error) {
    if (isApiError(error) && error.code === 'not_found') throw notFound();
    throw error;
  }
}

export const currenciesListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'currencies',
  validateSearch: currenciesSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_CURRENCIES'),
  loaderDeps: ({ search }) => search,
  // Starts the fetch without blocking: the page shows the previous rows, dimmed, until it lands.
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(currenciesListQuery(deps));
  },
  component: lazyRouteComponent(() => import('./pages/currencies-list-page'), 'CurrenciesListPage'),
});

export const currencyCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'currencies/new',
  // Creating checks the code is free first, which reads the single-currency route.
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_CURRENCIES', 'UPDATE_CURRENCIES'),
  staticData: { crumbs: () => [{ label: 'New currency' }] },
  component: lazyRouteComponent(() => import('./pages/currency-create-page'), 'CurrencyCreatePage'),
});

export const currencyDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'currencies/$code',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_CURRENCIES'),
  loader: ({ context, params }) => loadCurrency(context, params.code),
  staticData: { crumbs: (params) => [{ label: params.code ?? '' }] },
  notFoundComponent: CurrencyNotFound,
  component: lazyRouteComponent(() => import('./pages/currency-detail-page'), 'CurrencyDetailPage'),
});

export const currencyEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'currencies/$code/edit',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_CURRENCIES', 'UPDATE_CURRENCIES'),
  loader: ({ context, params }) => loadCurrency(context, params.code),
  staticData: {
    crumbs: (params) => [
      { label: params.code ?? '', href: `/currencies/${encodeURIComponent(params.code ?? '')}` },
      { label: 'Edit' },
    ],
  },
  notFoundComponent: CurrencyNotFound,
  component: lazyRouteComponent(() => import('./pages/currency-edit-page'), 'CurrencyEditPage'),
});

export const currenciesRoutes = [
  currenciesListRoute,
  currencyCreateRoute,
  currencyDetailRoute,
  currencyEditRoute,
] as const;
